import { useState } from 'react'
import { lazy } from 'react'
import { KeycloakAdminService } from '@nv/auth'
import { CheckBox, FormikError, Loader, NVButton, Select, TextField } from '@nv/basic-components'
import { AlertBanner, PhoneInput, useNVModal } from '@nv/components'
import { useConfigurationContext } from '@nv/contexts'
import { AddressInput, MailingAddressInput } from '@nv/pages'
import { AdminVoterService } from '@nv/services'
import { days, months, years } from '@nv/utils'
import { useFormik } from 'formik'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import * as Yup from 'yup'
import styles from './AdminAddElector.module.scss'

const NVBasicModal = lazy(() => import('@nv/lazy-loadable/lib/modals/NVBasicModal'))

export const AdminAddElector = () => {
  const { t } = useTranslation()
  const [successVoterId, setSuccessVoterId] = useState('')
  const [serverError, setServerError] = useState('')
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const { NVModal, openModal } = useNVModal()
  const {
    electionDate,
    minAge,

    includeSingleNameField,
    knownIdNumberTextFieldConfig,
    mailingAddressRequiredVoteByMail,
    enableDifferentMailingAddress,
    defaultVotingMethod,
  } = useConfigurationContext()

  const testMultipleRegexes = (regexArr, str) => {
    for (var regex of regexArr) {
      if (regex.test(str)) {
        return true
      }
    }
    return false
  }

  const votingMethodOptions =
    KeycloakAdminService.isSuperAdmin() || KeycloakAdminService.isVBMAdmin()
      ? [
          { label: t('admin.electorsList.votedChannelOptions.mail'), value: 'mail' },
          { label: t('admin.electorsList.votedChannelOptions.inPerson'), value: 'inPerson' },
          // { label: t('admin.electorsList.votedChannelOptions.online'), value: 'online' },
        ]
      : KeycloakAdminService.isContactInfoRoleAdmin() || KeycloakAdminService.isMonitoringAdmin()
      ? [{ label: t('admin.electorsList.votedChannelOptions.inPerson'), value: 'inPerson' }]
      : [{ label: t('admin.electorsList.votedChannelOptions.mail'), value: 'mail' }]

  const validationSchema = Yup.object()
    .shape(
      {
        firstName: Yup.string().required(t('admin.users.firstNameRequired')),
        lastName: Yup.string().when('singleName', {
          is: false,
          then: Yup.string().required(t('admin.users.lastNameRequired')),
          otherwise: Yup.string(),
        }),

        email: Yup.string().when(['votingChannel', 'phone'], {
          is: (votingChannel, phone) => votingChannel === 'inPerson' || phone,
          then: () => Yup.string(),
          otherwise: () => Yup.string().email(t('admin.users.invalidEmail')).required(t('form.validations.email')),
        }),
        confirmEmail: Yup.string().when(['votingChannel', 'email'], {
          is: (votingChannel, email) => votingChannel === 'inPerson' || !email,
          then: () => Yup.string(),
          otherwise: () =>
            Yup.string()
              .oneOf([Yup.ref('email'), null], t('admin.users.emailsMustMatch'))
              .required(t('admin.users.confirmEmailRequired')),
        }),
        phone: Yup.object().when(['votingChannel', 'email'], {
          is: (votingChannel, email) => votingChannel === 'inPerson' || email,
          then: () => Yup.string(),
          otherwise: () => Yup.string().required(t('form.validations.phone')),
        }),
        dobMonth:
          KeycloakAdminService.isMonitoringAdmin() || KeycloakAdminService.isVBMAdmin() || KeycloakAdminService.isSuperAdmin()
            ? Yup.string()
            : Yup.string().required(t('admin.users.monthRequired')),
        dobDay:
          KeycloakAdminService.isMonitoringAdmin() || KeycloakAdminService.isVBMAdmin() || KeycloakAdminService.isSuperAdmin()
            ? Yup.string()
            : Yup.string().required(t('admin.users.dayRequired')),
        dobYear:
          KeycloakAdminService.isMonitoringAdmin() || KeycloakAdminService.isVBMAdmin() || KeycloakAdminService.isSuperAdmin()
            ? Yup.string()
            : Yup.string().required(t('admin.users.yearRequired')),
        singleName: Yup.boolean(),
        addressNotListed: Yup.boolean(),
        searchAddress: Yup.object().when('addressNotListed', {
          is: false,
          then: () =>
            Yup.object().shape({
              streetAddress: Yup.string().required(t('form.validations.address')),
            }),
          otherwise: () => Yup.object().notRequired(),
        }),
        address: Yup.object().when('addressNotListed', {
          is: true,
          then: () =>
            Yup.object().shape({
              streetAddress: Yup.string().required(t('form.validations.street')),
              unitNumberPOBox: Yup.string().notRequired(),
              city: Yup.string().required(t('form.validations.city')),
              province: Yup.string().required(t('form.validations.province')),
              postalCode: Yup.string().required(t('form.validations.postalCode')),
            }),
          otherwise: () => Yup.object().notRequired(),
        }),
        mailingAddressNotListed: Yup.boolean(),
        differentMailingAddress: Yup.boolean(),
        searchMailingAddress: Yup.object().when(['differentMailingAddress', 'mailingAddressNotListed', 'votingChannel'], {
          is: (differentMailingAddress, mailingAddressNotList, votingChannel) =>
            (differentMailingAddress && !mailingAddressNotList) ||
            (votingChannel === 'mail' && mailingAddressRequiredVoteByMail && !mailingAddressNotList),
          then: () =>
            Yup.object().shape({
              formattedAddress: Yup.string().required(t('form.validations.address')),
            }),
          otherwise: () => Yup.object().notRequired(),
        }),
        mailingAddress: Yup.object().when(['differentMailingAddress', 'mailingAddressNotListed', 'votingChannel'], {
          is: (differentMailingAddress, mailingAddressNotList, votingChannel) =>
            (differentMailingAddress && mailingAddressNotList) ||
            (votingChannel === 'mail' && mailingAddressRequiredVoteByMail && mailingAddressNotList),
          then: () =>
            Yup.object().shape({
              streetAddress: Yup.string().required(t('form.validations.street')),
              unitNumberPOBox: Yup.string().notRequired(),
              city: Yup.string().required(t('form.validations.city')),
              province: Yup.string().when(['country'], {
                is: (country) =>
                  !testMultipleRegexes([/US/i, /USA/i, /UNITED STATES/i, /UNITED STATES OF AMERICA/i, /CA/i, /CANADA/i], country),
                then: () => Yup.string(),
                otherwise: () => Yup.string().required(t('form.validations.province')),
              }),

              postalCode: Yup.string().required(t('form.validations.postalCode')),
            }),
          otherwise: () => Yup.object().notRequired(),
        }),
        electionConfirmations: Yup.object().when(['votingChannel'], {
          is: (votingChannel) => votingChannel === 'mail',
          then: () =>
            Yup.object()
              .shape({
                eligiblePSB: Yup.boolean(),
                eligibleCSLF: Yup.boolean(),
                eligibleNone: Yup.boolean(),
              })
              .test('at-least-one-true', t('form.validations.oneConfirmationIsRequired'), function (value) {
                return value.eligiblePSB || value.eligibleCSLF || value.eligibleNone
              }),
          otherwise: () => Yup.object().notRequired(),
        }),
        votingChannel: Yup.string().required(t('form.validations.votingMethodRequired')),
      },
      [['email', 'phone']],
    )
    .test('is-valid-date', function (value) {
      if (KeycloakAdminService.isMonitoringAdmin() || KeycloakAdminService.isVBMAdmin() || KeycloakAdminService.isSuperAdmin()) return true
      const { dobDay, dobMonth, dobYear } = value
      if (!dobDay || !dobMonth || !dobYear) return false
      const day = parseInt(dobDay)
      const month = parseInt(dobMonth)
      const year = parseInt(dobYear)
      const date = new Date(year, month - 1, day)
      const isValidDate = date.getDate() === day && date.getMonth() === month - 1 && date.getFullYear() === year
      return isValidDate || this.createError({ path: 'dobYear', message: t('registration.form.validations.validBirthDate') })
    })
    .test('is-over-18', function (value) {
      if (KeycloakAdminService.isMonitoringAdmin() || KeycloakAdminService.isVBMAdmin() || KeycloakAdminService.isSuperAdmin()) return true
      const { dobDay, dobMonth, dobYear } = value
      if (!dobDay || !dobMonth || !dobYear) return false
      const day = parseInt(dobDay)
      const month = parseInt(dobMonth)
      const year = parseInt(dobYear)
      const dateCompareBirthday = electionDate ? electionDate : new Date()
      const birthDate = new Date(year, month - 1, day)
      const age = dateCompareBirthday.getFullYear() - birthDate.getFullYear()
      const monthDiff = dateCompareBirthday.getMonth() - birthDate.getMonth()
      const dayDiff = dateCompareBirthday.getDate() - birthDate.getDate()
      const isOverMinAge = age > minAge || (age === minAge && (monthDiff > 0 || (monthDiff === 0 && dayDiff >= 0)))
      return isOverMinAge || this.createError({ path: 'dobYear', message: t('registration.form.validations.minAge', { age: minAge }) })
    })
    .test('at-least-one-mail', null, function (value) {
      if (
        value.votingChannel === 'inPerson' ||
        value.mailVoteByMailPackage ||
        (value.pickupVoteByMailPackage && !(value.mailVoteByMailPackage && value.pickupVoteByMailPackage))
      ) {
        return true
      }
      return new Yup.ValidationError(t('registration.form.errors.noBallotReceiveMethodChosen'), null, 'pickupVoteByMailPackage')
    })
    .test('at-least-one-ballot-type', null, function (value) {
      return (
        value.votingChannel === 'inPerson' ||
        Object.values(value.ballotOptIns).some((value) => value === true) ||
        new Yup.ValidationError(t('form.validations.oneConfirmationIsRequired'), null, 'ballotOptIns.none')
      )
    })
  const formik = useFormik({
    initialValues: {
      firstName: '',
      middleName: '',
      lastName: '',
      singleName: false,
      dobMonth: '',
      dobDay: '',
      dobYear: '',
      phone: '',
      email: '',
      confirmEmail: '',
      address: {
        streetAddress: '',
        unitNumberPOBox: '',
        city: '',
        province: '',
        postalCode: '',
      },
      searchAddress: {
        streetAddress: '',
        unitNumberPOBox: '',
        city: '',
        province: '',
        postalCode: '',
        formattedAddress: '',
        // _id: '',
        value: '',
      },
      mailingAddress: {
        streetAddress: '',
        unitNumberPOBox: '',
        city: '',
        province: '',
        postalCode: '',
        country: '',
      },
      searchMailingAddress: {
        streetAddress: '',
        unitNumberPOBox: '',
        city: '',
        province: '',
        postalCode: '',
        country: '',
        formattedAddress: '',
        value: '',
      },
      electionConfirmations: {
        eligiblePSB: false,
        eligibleCSLF: false,
        eligibleNone: false,
      },
      votingChannel:
        KeycloakAdminService.isContactInfoRoleAdmin() || KeycloakAdminService.isMonitoringAdmin()
          ? 'inPerson'
          : // : KeycloakAdminService.isVBMAdmin()
            // ? 'mail'
            '',
      knownIdNumber: '',
      addressNotListed: false,
      mailingAddressNotListed: false,
      differentMailingAddress: false,
      mailVoteByMailPackage: false,
      pickupVoteByMailPackage: false,
      ballotOptIns: {
        mayor: false,
        councillor: false,
        none: false,
      },
    },
    validationSchema,
    onSubmit: async (values) => {
      setServerError('')
      let electionOptIns = ''
      for (var key in values.electionConfirmations) {
        if (values.electionConfirmations[key]) {
          if (electionOptIns.length) {
            electionOptIns += ',' + key
          } else {
            electionOptIns += key
          }
        }
      }

      const data = {
        ...values,
        electionOptIns,
        dateOfBirth:
          (KeycloakAdminService.isMonitoringAdmin() || KeycloakAdminService.isVBMAdmin() || KeycloakAdminService.isSuperAdmin()) &&
          !values.dobYear
            ? null
            : `${values.dobYear}-${values.dobMonth.toString().padStart(2, '0')}-${values.dobDay.toString().padStart(2, '0')}`,
        address: values.addressNotListed ? values.address : values.searchAddress,
        voteByMailPickup: values.pickupVoteByMailPackage ? true : false,
      }
      if (values.differentMailingAddress || mailingAddressRequiredVoteByMail) {
        data.mailingAddress = values.mailingAddressNotListed ? values.mailingAddress : values.searchMailingAddress
      }

      const ballotOptInsArr = Object.keys(values.ballotOptIns).filter((key) => values.ballotOptIns[key] === true)
      //@ts-ignore
      data.ballotOptIns = ballotOptInsArr.toString()
      setLoading(true)
      const registered = await AdminVoterService.registerVoter(data)
      if (registered.success) {
        setLoading(false)
        // openModal()
        setSuccessVoterId(registered.data?.voterIdentifier)
        navigate(`/admin/registrant-details/${registered.data?.voterIdentifier}`)
      } else {
        setLoading(false)
        switch (registered.error?.errorCode) {
          case 'REGISTERED':
            setServerError('REGISTERED')
            break
          default:
            setServerError('INELIGIBLE')
            break
        }
      }
      setLoading(false)
    },
  })
  const formattAddress = (address) => {
    if (!address) {
      return ''
    }
    if (address.formattedAddress) {
      if (address.unitNumberPOBox) {
        return `${address.unitNumberPOBox} - ${address.formattedAddress}`
      }
      return address.formattedAddress
    }
    if (address.unitNumberPOBox) {
      return `${address.unitNumberPOBox} - ${address.streetAddress}, ${address.city}, ${
        address.province ? address.province : address.province
      }, ${address.postalCode}`
    }
    return `${address.streetAddress}, ${address.city}, ${address.province}, ${address.postalCode}`
  }

  return (
    <>
      <NVModal>
        <NVBasicModal
          title={t('admin.users.addNewElector')}
          text={t('admin.electorsList.newElectorSuccessfullyAdded')}
          success={true}
          navigateTo={`/admin/registrant-details/${successVoterId}`}
        />
      </NVModal>

      <div className={styles['container']}>
        <h1 className={styles['header']}>{t('admin.users.addNewElector')}</h1>
        <form onSubmit={formik.handleSubmit} className={styles['form']}>
          <div className={styles['hr']}>{t('registration.votingMethod')}</div>
          <div className={styles['form__voting-method']}>
            <Select
              label={t('registration.selectVotingChannel')}
              formik={formik}
              options={votingMethodOptions}
              // custom
              field='votingChannel'
              value={formik.values.votingChannel}
              isRequired={true}
            />
          </div>
          <div className={styles['hr']}>{t('registration.basicInfo')}</div>
          {/* Name Section */}
          <div className={styles['form__name']}>
            <TextField
              formik={formik}
              type='text'
              field='firstName'
              placeholder={t('form.labels.firstName')}
              label={t('form.labels.firstName')}
              isRequired={true}
              autocomplete='off'
            />
            <TextField
              formik={formik}
              type='text'
              field='middleName'
              placeholder={t('form.labels.middleName')}
              label={t('form.labels.middleName')}
              isRequired={false}
              autocomplete='off'
            />
            <TextField
              formik={formik}
              type='text'
              field='lastName'
              placeholder={t('form.labels.lastName')}
              label={t('form.labels.lastName')}
              isRequired={!formik.values.singleName}
              autocomplete='off'
            />
          </div>
          {includeSingleNameField && (
            <CheckBox
              formik={formik}
              field='singleName'
              checked={formik.values.singleName}
              onChange={() => formik.setFieldValue('singleName', !formik.values.singleName)}
              label={t('registration.singleName')}
              square={true}
            />
          )}
          {/* Date of Birth */}
          <div className={styles['form__birth']}>
            <div className={styles['form__select-container']}>
              <div className={styles['form__item-title']}>
                {t('form.labels.dateOfBirth')} <span className={styles['form__required']}>*</span>
              </div>
              <div className={styles['form__selects']}>
                <Select
                  label={t('countdown.time.calendar.month')}
                  formik={formik}
                  options={months()}
                  field='dobMonth'
                  value={formik.values.dobMonth}
                  isRequired={
                    KeycloakAdminService.isMonitoringAdmin() || KeycloakAdminService.isVBMAdmin() || KeycloakAdminService.isSuperAdmin()
                      ? false
                      : true
                  }
                  ariaLabel={t('registration.dateOfBirthMonth')}
                  isSearchable={true}
                />
                <Select
                  label={t('countdown.time.calendar.day')}
                  formik={formik}
                  options={days}
                  field='dobDay'
                  value={formik.values.dobDay}
                  isRequired={
                    KeycloakAdminService.isMonitoringAdmin() || KeycloakAdminService.isVBMAdmin() || KeycloakAdminService.isSuperAdmin()
                      ? false
                      : true
                  }
                  ariaLabel={t('registration.dateOfBirthDay')}
                  isSearchable={true}
                />
                <Select
                  label={t('countdown.time.calendar.year')}
                  formik={formik}
                  options={years}
                  field='dobYear'
                  value={formik.values.dobYear}
                  isRequired={
                    KeycloakAdminService.isMonitoringAdmin() || KeycloakAdminService.isVBMAdmin() || KeycloakAdminService.isSuperAdmin()
                      ? false
                      : true
                  }
                  ariaLabel={t('registration.dateOfBirthYear')}
                  isSearchable={true}
                />
              </div>
            </div>
          </div>

          {formik.values.votingChannel !== 'inPerson' && (
            <>
              <div className={styles['hr']}>{t('registration.contactInformation')}</div>
              <div className={styles['form__email-section']}>
                <TextField
                  formik={formik}
                  type='email'
                  field='email'
                  placeholder={t('form.labels.email')}
                  label={t('form.labels.email')}
                  isRequired={formik.values.votingChannel !== 'inPerson' && !formik.values.phone}
                  autocomplete='off'
                />
                <TextField
                  formik={formik}
                  type='email'
                  field='confirmEmail'
                  placeholder={t('form.labels.confirmEmail')}
                  label={t('form.labels.confirmEmail')}
                  isRequired={formik.values.votingChannel !== 'inPerson' && formik.values.email}
                  autocomplete='off'
                />
              </div>
              <div className={styles['form__phone-section']}>
                <PhoneInput formik={formik} field='phone' autocomplete='off' isRequired={!formik.values.email} />
              </div>
            </>
          )}
          <div className={styles['hr']}>{t('registration.qualifyingResidentialAddress')}</div>
          <AddressInput formik={formik} styles={styles} autocomplete='off' />

          {formik.values.votingChannel === 'mail' && (
            <>
              <div className={styles['hr']}>{t('registration.receiveMailBallotPackage')}</div>
              <CheckBox
                formik={formik}
                field='mailVoteByMailPackage'
                checked={formik.values.mailVoteByMailPackage}
                onChange={() => {
                  if (!formik.values.mailVoteByMailPackage) {
                    formik.setFieldValue('pickupVoteByMailPackage', false)
                  }
                  formik.setFieldValue('mailVoteByMailPackage', !formik.values.mailVoteByMailPackage)
                }}
                label={t('registration.deliverMailBallotPackageCheck')}
                ariaLabel={t('registration.deliverMailBallotPackageCheck')}
                square={true}
              />

              {/* Vote by mail delivery address */}
              {formik.values.mailVoteByMailPackage && enableDifferentMailingAddress ? (
                <>
                  <div className={styles['form__subheader']}>{t('registration.mailingAddress')}</div>
                  <CheckBox
                    formik={formik}
                    field='differentMailingAddress'
                    checked={!formik.values.differentMailingAddress}
                    onChange={() => formik.setFieldValue('differentMailingAddress', !formik.values.differentMailingAddress)}
                    label={t('registration.sameMailingAddress')}
                    ariaLabel={t('registration.sameMailingAddress')}
                    square={true}
                  />
                  {!formik.values.differentMailingAddress && (
                    <div className={styles['residentialAddress']}>
                      {!formik.values?.addressNotListed
                        ? formattAddress(formik.values?.searchAddress)
                        : formattAddress(formik.values?.address)}
                    </div>
                  )}
                  <CheckBox
                    formik={formik}
                    field='differentMailingAddress'
                    checked={formik.values.differentMailingAddress}
                    onChange={() => formik.setFieldValue('differentMailingAddress', !formik.values.differentMailingAddress)}
                    label={t('registration.differentMailingAddress')}
                    ariaLabel={t('registration.differentMailingAddress')}
                    square={true}
                  />
                  {formik.values.differentMailingAddress && (
                    <MailingAddressInput
                      formik={formik}
                      styles={styles}
                      addressFieldName='mailingAddress'
                      searchAddressFieldName='searchMailingAddress'
                      addressNotListedFieldName='mailingAddressNotListed'
                      autocomplete='off'
                    />
                  )}
                  <div>{t('registration.deliverMailBallotPackageNote')}</div>
                </>
              ) : null}

              {/* Check box for vote by mail package to be picked up*/}
              <CheckBox
                formik={formik}
                field='pickupVoteByMailPackage'
                checked={formik.values.pickupVoteByMailPackage}
                onChange={() => {
                  if (!formik.values.pickupVoteByMailPackage) {
                    formik.setFieldValue('mailVoteByMailPackage', false)
                  }
                  formik.setFieldValue('pickupVoteByMailPackage', !formik.values.pickupVoteByMailPackage)
                }}
                label={t('registration.pickupMailBallotPackageCheck')}
                ariaLabel={t('registration.pickupMailBallotPackageCheck')}
                square={true}
              />
              {/* Vote by mail pickup */}
              {formik.values.pickupVoteByMailPackage && (
                <div>
                  <strong>{t('registration.note')} </strong>
                  {t('registration.pickupMailBallotPackageNote')}
                  <br /> <br />
                  <strong>{t('registration.pickupLocation.title')}</strong>
                  <br />
                  {t('registration.pickupLocation.line1')}
                  <br />
                  {t('registration.pickupLocation.line2')}
                  <br />
                  {t('registration.pickupLocation.line3')}
                  <br />
                  {t('registration.pickupLocation.line4')}
                  <br />
                  {t('registration.pickupLocation.line5')}
                  <br />
                </div>
              )}

              <div>
                <div className={styles['hr']} style={{ marginBottom: 20 }}>
                  {t('registration.requestingVoteByMailPackage')}
                </div>

                <div style={{ marginLeft: 15 }}>
                  <p style={{ fontSize: 16, fontWeight: 500 }}>{t('registration.requestVoteByMailPackageForBallots')}</p>
                  <CheckBox
                    formik={formik}
                    checked={formik.values.ballotOptIns.mayor}
                    field='ballotOptIns.mayor'
                    onChange={() => {
                      if (!formik.values.ballotOptIns.mayor) {
                        formik.setFieldValue('ballotOptIns.none', false)
                      }
                      formik.setFieldValue('ballotOptIns.mayor', !formik.values.ballotOptIns.mayor)
                    }}
                    label={t('registration.ballots.ballot1')}
                    ariaLabel={t('registration.ballots.ballot1')}
                    square={true}
                  />
                  <CheckBox
                    formik={formik}
                    checked={formik.values.ballotOptIns.councillor}
                    field='ballotOptIns.councillor'
                    onChange={() => {
                      if (!formik.values.ballotOptIns.councillor) {
                        formik.setFieldValue('ballotOptIns.none', false)
                      }
                      formik.setFieldValue('ballotOptIns.councillor', !formik.values.ballotOptIns.councillor)
                    }}
                    label={t('registration.ballots.ballot2')}
                    ariaLabel={t('registration.ballots.ballot2')}
                    square={true}
                  />
                  <CheckBox
                    formik={formik}
                    checked={formik.values.ballotOptIns.none}
                    field='ballotOptIns.none'
                    onChange={() => {
                      if (!formik.values.ballotOptIns.none) {
                        formik.setFieldValue('ballotOptIns.mayor', false)
                        formik.setFieldValue('ballotOptIns.councillor', false)
                      }
                      formik.setFieldValue('ballotOptIns.none', !formik.values.ballotOptIns.none)
                    }}
                    label={t('registration.ballots.ballot3')}
                    ariaLabel={t('registration.ballots.ballot3')}
                    square={true}
                  />
                </div>
              </div>

              {/* School boards start */}

              <>
                <div className={styles['hr']}>{t('admin.electorsDetails.registeredVotingDistricts')}</div>

                <CheckBox
                  checked={formik.values.electionConfirmations.eligiblePSB}
                  onChange={() => {
                    if (!formik.values.electionConfirmations.eligiblePSB) {
                      formik.setFieldValue('electionConfirmations.eligibleCSLF', false)
                      formik.setFieldValue('electionConfirmations.eligibleNone', false)
                    }
                    formik.setFieldValue('electionConfirmations.eligiblePSB', !formik.values.electionConfirmations.eligiblePSB)
                  }}
                  label={t('registration.confirmEligibleVoteInPSB')}
                  square={true}
                />
                <CheckBox
                  checked={formik.values.electionConfirmations.eligibleCSLF}
                  onChange={() => {
                    if (!formik.values.electionConfirmations.eligibleCSLF) {
                      formik.setFieldValue('electionConfirmations.eligiblePSB', false)
                      formik.setFieldValue('electionConfirmations.eligibleNone', false)
                    }
                    formik.setFieldValue('electionConfirmations.eligibleCSLF', !formik.values.electionConfirmations.eligibleCSLF)
                  }}
                  label={t('registration.confirmEligibleVoteInCSLF')}
                  square={true}
                />
                <CheckBox
                  checked={formik.values.electionConfirmations.eligibleNone}
                  onChange={() => {
                    if (!formik.values.electionConfirmations.eligibleNone) {
                      formik.setFieldValue('electionConfirmations.eligiblePSB', false)
                      formik.setFieldValue('electionConfirmations.eligibleCSLF', false)
                    }
                    formik.setFieldValue('electionConfirmations.eligibleNone', !formik.values.electionConfirmations.eligibleNone)
                  }}
                  label={t('registration.confirmEligibleVoteInNone')}
                  square={true}
                />
                <FormikError formik={formik} fieldName={'electionConfirmations'} />
              </>
            </>
          )}
          {/* School boards end */}
          <div className={styles['divider']}></div>

          {(serverError === 'REGISTERED' || serverError === 'INELIGIBLE') && (
            <AlertBanner text={t(`registration.form.errors.${serverError.toLocaleLowerCase()}`)} error={true} />
          )}
          {/* Buttons */}
          <div className={styles['form__actions']}>
            <NVButton variant='primary' text={t('buttons.clearForm')} height={50} onClick={formik.handleReset} />
            {loading ? <Loader /> : <NVButton text={t('buttons.add')} height={50} onClick={formik.handleSubmit} />}
          </div>
          <div className={styles['privacyStatement']}>{t('registration.privacyStatement')}</div>
        </form>
      </div>
    </>
  )
}
